// LoginPage — email/password sign-in.
//
// Rendered by AuthGate (in App.tsx) whenever no Firebase user is signed in.
// On successful sign-in, onAuthStateChanged fires inside firebase.ts, AuthGate
// swaps to <Routes> and this component unmounts — no router push or
// onSuccess callback needed.
//
// Self-registration is intentionally disabled in v2.0.0 — Justin adds users
// in Firebase Console (Auth → Users → Add user). If we ever want public
// sign-up, restore the register branch + the "Create account" toggle. The
// createAccountWithEmail helper is still exported from firebase.ts.

import { useState } from 'react';
import { signInWithEmail } from '@/shared/firebase';
import { config } from '@/shared/config';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }

    setBusy(true);
    try {
      await signInWithEmail(email, password);
      // Success — AuthGate's observeAuthState callback unmounts this component.
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-paper text-text font-sans flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="text-3xl font-bold mb-1">
            <span className="text-orange">AI</span>{' '}
            <span className="text-navy">QUOTE</span>{' '}
            <span className="text-xs bg-orange text-white px-2 py-0.5 rounded font-mono align-middle">
              v{config.appVersion}
            </span>
          </div>
          <p className="text-sm text-text-mute">Sign in to continue</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-paper border border-line-dark rounded shadow-sm p-6"
        >
          <label className="block mb-4">
            <span className="text-xs font-semibold text-navy block mb-1">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              autoComplete="email"
              autoFocus
              className="w-full px-3 py-2 border border-line-dark rounded text-sm focus:outline-none focus:border-orange disabled:bg-bg disabled:text-text-mute"
              placeholder="you@keymf.com"
            />
          </label>

          <label className="block mb-4">
            <span className="text-xs font-semibold text-navy block mb-1">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoComplete="current-password"
              className="w-full px-3 py-2 border border-line-dark rounded text-sm focus:outline-none focus:border-orange disabled:bg-bg disabled:text-text-mute"
            />
          </label>

          {error && (
            <div className="mb-4 bg-orange/15 border border-orange/40 text-orange text-sm px-3 py-2 rounded">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-orange text-white font-semibold py-2 rounded hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-xs text-text-mute mt-4">
          Need an account? Ask Justin to add you in Firebase Console.
        </p>
      </div>
    </div>
  );
}
